import { loadSpeechSettings, saveSpeechSettings, isSpeechSynthesisSupported, getAvailableVoices, } from './captionSpeechSettings';
const TEST_PHRASE = 'This is a preview of the selected caption voice.';
/**
 * Imperatively creates and shows a `<dialog-box>` web component for
 * configuring speech synthesis settings.
 *
 * @param onSettingsChange Called when the dialog is closed with the final settings.
 */
export function showSpeechSettingsDialog(onSettingsChange) {
    if (!isSpeechSynthesisSupported())
        return;
    const settings = loadSpeechSettings();
    // --- Create dialog ---
    const dialog = document.createElement('dialog-box');
    dialog.setAttribute('dialog-title', 'Speech Settings');
    dialog.setAttribute('width', '380');
    dialog.setAttribute('height', '480');
    // --- Content ---
    const content = document.createElement('div');
    content.slot = 'content';
    content.style.padding = '12px 16px';
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.gap = '14px';
    // Voice
    const voiceLabel = document.createElement('label');
    voiceLabel.textContent = 'Voice';
    voiceLabel.style.fontWeight = 'bold';
    voiceLabel.style.fontSize = '13px';
    const voiceSelect = document.createElement('select');
    voiceSelect.style.width = '100%';
    voiceSelect.style.marginTop = '4px';
    const populateVoices = () => {
        const voices = getAvailableVoices();
        voiceSelect.innerHTML = '';
        const defaultOpt = document.createElement('option');
        defaultOpt.value = '';
        defaultOpt.textContent = 'System Default';
        voiceSelect.appendChild(defaultOpt);
        for (const voice of voices) {
            const opt = document.createElement('option');
            opt.value = voice.voiceURI;
            opt.textContent = `${voice.name} (${voice.lang})`;
            if (voice.voiceURI === settings.voiceURI)
                opt.selected = true;
            voiceSelect.appendChild(opt);
        }
    };
    populateVoices();
    if (getAvailableVoices().length === 0) {
        speechSynthesis.addEventListener('voiceschanged', populateVoices, { once: true });
    }
    const voiceGroup = document.createElement('div');
    voiceGroup.appendChild(voiceLabel);
    voiceGroup.appendChild(voiceSelect);
    content.appendChild(voiceGroup);
    // Volume
    const volumeLabel = document.createElement('label');
    volumeLabel.style.fontWeight = 'bold';
    volumeLabel.style.fontSize = '13px';
    const volumeValue = document.createElement('span');
    volumeValue.style.fontWeight = 'normal';
    volumeValue.textContent = ` ${Math.round(settings.volume * 100)}%`;
    volumeLabel.textContent = 'Volume';
    volumeLabel.appendChild(volumeValue);
    const volumeSlider = document.createElement('input');
    volumeSlider.type = 'range';
    volumeSlider.min = '0';
    volumeSlider.max = '1';
    volumeSlider.step = '0.1';
    volumeSlider.value = String(settings.volume);
    volumeSlider.style.width = '100%';
    volumeSlider.style.marginTop = '4px';
    volumeSlider.addEventListener('input', () => {
        volumeValue.textContent = ` ${Math.round(parseFloat(volumeSlider.value) * 100)}%`;
    });
    const volumeGroup = document.createElement('div');
    volumeGroup.appendChild(volumeLabel);
    volumeGroup.appendChild(volumeSlider);
    content.appendChild(volumeGroup);
    // Rate
    const rateLabel = document.createElement('label');
    rateLabel.style.fontWeight = 'bold';
    rateLabel.style.fontSize = '13px';
    const rateValue = document.createElement('span');
    rateValue.style.fontWeight = 'normal';
    rateValue.textContent = ` ${settings.rate.toFixed(1)}x`;
    rateLabel.textContent = 'Rate';
    rateLabel.appendChild(rateValue);
    const rateSlider = document.createElement('input');
    rateSlider.type = 'range';
    rateSlider.min = '0.1';
    rateSlider.max = '2.0';
    rateSlider.step = '0.1';
    rateSlider.value = String(settings.rate);
    rateSlider.style.width = '100%';
    rateSlider.style.marginTop = '4px';
    rateSlider.addEventListener('input', () => {
        rateValue.textContent = ` ${parseFloat(rateSlider.value).toFixed(1)}x`;
    });
    const rateGroup = document.createElement('div');
    rateGroup.appendChild(rateLabel);
    rateGroup.appendChild(rateSlider);
    content.appendChild(rateGroup);
    // Playback Behavior
    const behaviorLabel = document.createElement('label');
    behaviorLabel.textContent = 'Playback Behavior';
    behaviorLabel.style.fontWeight = 'bold';
    behaviorLabel.style.fontSize = '13px';
    const behaviorSelect = document.createElement('select');
    behaviorSelect.style.width = '100%';
    behaviorSelect.style.marginTop = '4px';
    const behaviors = [
        { value: 'nothing', label: 'Do Nothing' },
        { value: 'lower-volume', label: 'Lower Volume While Speaking' },
        { value: 'pause', label: 'Pause While Speaking' },
    ];
    for (const b of behaviors) {
        const opt = document.createElement('option');
        opt.value = b.value;
        opt.textContent = b.label;
        if (b.value === settings.playbackBehavior)
            opt.selected = true;
        behaviorSelect.appendChild(opt);
    }
    const behaviorGroup = document.createElement('div');
    behaviorGroup.appendChild(behaviorLabel);
    behaviorGroup.appendChild(behaviorSelect);
    content.appendChild(behaviorGroup);
    // Overrun checkbox
    const overrunBox = document.createElement('check-box');
    overrunBox.setAttribute('text', 'Pause video if speech overruns caption');
    if (settings.pauseOnOverrun) {
        overrunBox.setAttribute('checked', 'checked');
    }
    const overrunGroup = document.createElement('div');
    overrunGroup.appendChild(overrunBox);
    // Hide when behavior is "pause" (since video is already paused)
    overrunGroup.style.display = settings.playbackBehavior === 'pause' ? 'none' : '';
    content.appendChild(overrunGroup);
    behaviorSelect.addEventListener('change', () => {
        overrunGroup.style.display = behaviorSelect.value === 'pause' ? 'none' : '';
    });
    dialog.appendChild(content);
    // --- Buttons ---
    const buttonsDiv = document.createElement('div');
    buttonsDiv.slot = 'buttons';
    const testBtn = document.createElement('button');
    testBtn.textContent = 'Test Voice';
    testBtn.type = 'button';
    testBtn.addEventListener('click', () => {
        speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(TEST_PHRASE);
        utterance.rate = parseFloat(rateSlider.value);
        utterance.volume = parseFloat(volumeSlider.value);
        const voiceURI = voiceSelect.value;
        if (voiceURI) {
            const voices = speechSynthesis.getVoices();
            const match = voices.find((v) => v.voiceURI === voiceURI);
            if (match)
                utterance.voice = match;
        }
        speechSynthesis.speak(utterance);
    });
    buttonsDiv.appendChild(testBtn);
    dialog.appendChild(buttonsDiv);
    // --- Close handler ---
    dialog.autoKill = true;
    dialog.onCloseHandler = () => {
        // Cancel any test speech
        speechSynthesis.cancel();
        const newSettings = {
            enabled: settings.enabled,
            voiceURI: voiceSelect.value,
            rate: parseFloat(rateSlider.value),
            volume: parseFloat(volumeSlider.value),
            playbackBehavior: behaviorSelect.value,
            pauseOnOverrun: overrunBox.checked,
        };
        saveSpeechSettings(newSettings);
        onSettingsChange(newSettings);
    };
    document.body.appendChild(dialog);
    dialog.show();
}
